import { Component, type ErrorInfo, type ReactNode } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { Button, Result } from "antd";

interface ChunkLoadErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ChunkLoadErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary that catches dynamic import failures (stale chunks)
 * and provides a fallback with the ability to refresh the page
 */
class ChunkLoadErrorBoundary extends Component<ChunkLoadErrorBoundaryProps, ChunkLoadErrorBoundaryState> {
  constructor(props: ChunkLoadErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ChunkLoadErrorBoundaryState {
    // Check if this is a chunk loading error
    const isChunkLoadError =
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Loading chunk") ||
      error.message.includes("ChunkLoadError") ||
      error.name === "ChunkLoadError" ||
      ("code" in error &&
        typeof error.code === "string" &&
        (error.code === "ERR_NETWORK" ||
          error.code === "NETWORK_ERROR" ||
          error.code === "ECONNREFUSED" ||
          error.code === "ECONNRESET" ||
          error.code === "ENOTFOUND")) ||
      (typeof error.message === "string" &&
        (error.message.includes("NetworkError") ||
          error.message.includes("Failed to fetch") ||
          error.message.includes("ERR_CONNECTION") ||
          error.message.includes("net::ERR_INTERNET_DISCONNECTED")));

    return {
      hasError: isChunkLoadError,
      error: isChunkLoadError ? error : undefined,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only log chunk loading errors
    if (this.state.hasError) {
      Sentry.logger.warn("Chunk loading error detected:", { error, errorInfo });
    } else {
      // Log non-chunk errors and let them propagate naturally
      Sentry.logger.info("Non-chunk error caught in ChunkLoadErrorBoundary:", { error, errorInfo });
    }
  }

  handleRefresh = () => {
    // Refresh the page to load the latest chunks
    window.location.reload();
  };

  handleRetry = () => {
    // Reset the error boundary state to retry
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback or default error UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Result
          status="warning"
          title="Aplikacja została zaktualizowana"
          subTitle="Aby zobaczyć najnowszą wersję, odśwież stronę."
          extra={[
            <Button key="refresh" type="primary" icon={<ReloadOutlined />} onClick={this.handleRefresh}>
              Odśwież stronę
            </Button>,
            <Button key="retry" onClick={this.handleRetry}>
              Spróbuj ponownie
            </Button>,
          ]}
        />
      );
    }

    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
